// EventForm — the create / edit page for a calendar event (W40.1 F7).
//
// **A page, not a sheet.** It used to be `EventEditor`, a `BottomSheet` opened
// from the month view, and it is now the body of `/calendar/events/new` and
// `/calendar/events/:id/edit` — composed like the other six create pages
// (`anniversaries/new.tsx` is the reference): `useHidePillNav()`, `PageHero`
// with a 44pt back chevron, `FormErrorBanner`, `PageContainer` +
// `SectionStack`, `pb-40` clearing the docked bar, `PageBottomActionBar`, and a
// `useJsonDraft` cleared only once the server has the rows.
//
// Two decisions here are not reversible by refactoring:
//
// **Non-contiguous multi-select, and no drag.** A drag across a month grid
// cannot select 「10/5、10/9、10/20」 — a month of holiday with the workdays left
// out is the real case, and a drag either takes the workdays too or misses days
// the finger crossed quickly. The old `pointerenter` implementation did exactly
// that: reproduced with CDP touch events, dragging 10/5 → 10/7 selected
// 9/28 – 10/7. So the whole drag mechanism is gone and a tap toggles one day.
//
// **Contiguous → one row; scattered → N rows.** The storage model is
// `start_date` + nullable `end_date`, so the backend cannot express a
// non-contiguous set as one event at all. See `eventFanOut.ts` and ADR-0101.
// The month grid's read-back was proven safe *before* any of this was written
// (`backend/.../test_calendar_fanout_readback.py`), which is why no read-side
// change was needed.

import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";


import { PageHero } from "@/components/primitives/PageHero";
import { PageBottomActionBar } from "@/components/system/PageBottomActionBar";
import { useHidePillNav } from "@/components/system/BottomChrome";
import { FormErrorBanner } from "@/components/auth/FormField";
import { PageContainer, SectionStack } from "@/components/layout";
import { useJsonDraft } from "@/lib/hooks/useDraft";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api/pair";
import type { CalendarItem, OwnedCategory, OwnerToken } from "@/lib/api/calendar";
import {
  CATEGORY_ICON,
  CATEGORY_LABEL,
  VISIBILITY_LABEL,
} from "@/components/calendar/calendarIcons";
import { submitFanOut, type FanOutFields } from "./eventFanOut";
import { useCycleAutofill } from "./useCycleAutofill";
import { calendarEventFormSchema, isContiguous } from "@/lib/validation/calendar";
import { CALENDAR_RANGE_DAYS } from "@/lib/apiLimits";
import { cn } from "@/lib/utils/cn";

const OWNED: OwnedCategory[] = ["leave", "trip", "cycle", "manual"];
const WEEKDAY_SHORT = ["一", "二", "三", "四", "五", "六", "日"] as const;

export interface EventFormProps {
  /** Deep-link seed — the long-pressed day, or today. */
  initialDate?: string | null;
  /**
   * W58.1 — 從 `?category=` 帶進來的類型。
   *
   * 「一鍵補記」的入口：月曆上提示「經期比預期晚了」時，那顆按鈕要能直接
   * 開一個**已經是週期類型**的表單，而不是讓使用者自己去點一次「週期」。
   * 傳進來的值必須是 `OwnedCategory` 之一 —— 別的東西（例如舊的草稿裡的
   * 亂碼）一律當作沒帶。
   */
  initialCategory?: string | null;
  /**
   * Non-null on the edit route: the existing row to PATCH, in the same shape
   * the day sheet hands around.
   *
   * `CalendarItem` rather than `CalendarEvent` on purpose — `toEditorItem()`
   * already adapts a stored row into that shape (including resolving
   * `owner_id` → `me` / `partner` against the signed-in user), so reusing it
   * means the edit page and the day sheet cannot disagree about who owns an
   * event.
   */
  editing?: CalendarItem | null;
  /** Where the back affordance and a cancelled form go. */
  backTo: string;
  /**
   * Called only once every requested day exists server-side.
   *
   * `ids` is a list because a scattered selection creates several rows, and
   * the caller hands them to the undo bar — one 「復原」 has to take back all of
   * them, not the first.
   */
  onDone: (created: { ids: string[]; dayCount: number; isEdit: boolean }) => void;
}

interface FormState {
  title: string;
  category: OwnedCategory;
  owner: OwnerToken;
  visibility: "shared" | "private";
  days: string[];
  cursor: string;
}

export function EventForm({
  initialDate,
  initialCategory,
  editing,
  backTo,
  onDone,
}: EventFormProps) {
  // W35.6 — this page docks the submit action, so it owns the bottom edge and
  // the pill nav steps aside. This is also the first page whose layout depended
  // on that working: until W40.1 F6 the claim never reset, so arriving here once
  // took the nav away for the rest of the SPA session.
  useHidePillNav();
  const nav = useNavigate();
  const { push: pushToast } = useToast();

  const seed = editing?.start_date ?? initialDate ?? todayIso();
  // The draft covers the whole form (W39.76: drafting only the long field is the
  // wrong half-fix). Read synchronously, so a revoked session that drops you on
  // /login and back does not come home to a blank title.
  const draft = useJsonDraft<FormState>("calendar-event-form", {
    title: "",
    category: "leave",
    owner: "me",
    visibility: "shared",
    days: [],
    cursor: seed,
  });

  const [title, setTitle] = useState(editing?.title ?? draft.value.title);
  const [category, setCategory] = useState<OwnedCategory>(
    editing
      ? (editing.category as OwnedCategory)
      : (initialCategory && OWNED.includes(initialCategory as OwnedCategory)
          ? (initialCategory as OwnedCategory)
          : draft.value.category),
  );
  const [owner, setOwner] = useState<OwnerToken>(editing?.owner ?? draft.value.owner);
  const [visibility, setVisibility] = useState<"shared" | "private">(
    editing ? "shared" : draft.value.visibility,
  );
  const [days, setDays] = useState<string[]>(
    editing
      ? expandRow(editing.start_date, editing.end_date)
      : draft.value.days.length
        ? draft.value.days
        : [seed],
  );
  const [cursor, setCursor] = useState(draft.value.cursor || seed);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  /**
   * Days already created in an earlier, partly-successful attempt. A ref rather
   * than state because it is bookkeeping the user never sees, and because the
   * retry button must read the value from the moment the submit starts without
   * waiting for a re-render.
   */
  const createdRef = useRef<Map<string, string>>(new Map());
  /**
   * A counter mirroring `createdRef`, so the CTA can say 「重試剩下的 N 天」
   * during a render. Reading `createdRef.current` in render would show the
   * pre-submit value, because the ref is mutated inside an async callback with
   * no state change to trigger the re-render that would follow it.
   */
  const [createdCount, setCreatedCount] = useState(0);
  const queryClient = useQueryClient();

  const saveDraft = useCallback(
    (patch: Partial<FormState>) =>
      draft.save({ title, category, owner, visibility, days, cursor, ...patch }),
    [draft, title, category, owner, visibility, days, cursor],
  );

  /**
   * W58.0 — 週期事件的「自動計算時間」。
   *
   * 切到「週期」類型時，依平均經期長度把選取自動展開，並在標題空著時
   * 填上「經期」。邏輯在 `useCycleAutofill` 裡（這個檔案已經 23 KB，
   * §3 #10 的上限是 15 KB）。
   *
   * 放在 `saveDraft` **之後**是必要的：那兩個 callback 會用到它，而
   * `const` 沒有提升 —— 放在前面就是 TDZ。
   */
  const cycleAutofill = useCycleAutofill({
    category,
    days,
    title,
    isEdit: Boolean(editing?.id),
    onDaysChange: (next) => {
      setDays(next);
      saveDraft({ days: next });
    },
    onTitleChange: (next) => {
      setTitle(next);
      saveDraft({ title: next });
    },
  });

  function toggleDay(day: string) {
    const next = days.includes(day) ? days.filter((d) => d !== day) : [...days, day].sort();
    setDays(next);
    saveDraft({ days: next });
  }

  const selectedCount = days.length;
  const tooLong = selectedCount > CALENDAR_RANGE_DAYS;
  const miniDays = useMemo(() => buildMiniMonth(cursor), [cursor]);
  const isEdit = Boolean(editing?.id);

  const submit = useCallback(async () => {
    setFormError(null);
    const parsed = calendarEventFormSchema.safeParse({ title: title.trim(), category, owner, visibility, days });
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? "請檢查欄位");
      return;
    }
    setBusy(true);
    try {
      const fields: FanOutFields = {
        category: parsed.data.category,
        title: parsed.data.title,
        owner_id: parsed.data.owner,
        visibility: parsed.data.visibility,
      };
      const { created, failed, firstError } = await submitFanOut({
        fields,
        days: parsed.data.days,
        created: createdRef.current,
        editingId: editing?.id ?? null,
        queryClient,
      });

      if (failed.length > 0) {
        // D4 — partial success is stated, not hidden, and the page does not
        // navigate: leaving would throw away the selection that is half built.
        // The primary button becomes 「重試剩下的 N 天」 and `submitFanOut`'s
        // `created` map makes that button post only the days still missing.
        setCreatedCount(created.size);
        setFormError(
          `已建立 ${created.size}/${parsed.data.days.length} 天，${failed.length} 天失敗${
            firstError ? ` · ${firstError}` : ""
          }`,
        );
        return;
      }

      // W39.76 — cleared only once the rows exist server-side.
      draft.clear();
      // **成功提示由目的地說，不在這裡說**（ADR-0215）。
      //
      // 這裡推的 toast 是在**導航之後**才會被看見的 —— `onDone` 立刻跳走，而
      // `<ToastProvider>` 掛在 `main.tsx`（全 app 一份），所以這句話從來沒有
      // 出現在「建立事件」這一頁，它落在**目的地**頁上。
      //
      // 目的地有兩個，而它們的確認管道不同：
      //
      // - **新增** → 月曆頁，那裡有**撤銷條**：同一句話 + 一個「復原」按鈕
      //   （撤銷一次新增）。這裡再說一次等於**同一句話說兩遍**（而且撤銷條
      //   多給了動作），所以這裡不說。
      // - **修改** → 事件詳情頁，那裡**沒有**撤銷條（它的撤銷條屬於「刪除」）。
      //   所以由 `CalendarEventEdit` 自己推 toast。
      //
      // 失敗路徑的 toast 仍然在這裡 —— 失敗時**不會**導航，留在這一頁，
      // toast 是那一刻唯一能出現在使用者眼前的東西。
      onDone({ ids: [...created.values()], dayCount: created.size, isEdit });
    } catch (err) {
      // W50.0 §3 (A-3). This catch used to be the **only** place the failure
      // surfaced: `formError` renders inline in `FormErrorBanner`, and the
      // mutation is caught rather than rethrown — so the global
      // `MutationErrorReporter`'s `onError` never ran and the calendar was the
      // one create page in the app where a failed save produced no toast.
      //
      // Both channels, on purpose. The inline banner is the better form
      // affordance and stays. The toast is what every other create page does,
      // and it is the one that survives the user scrolling away from the form.
      //
      // Same reasoning as W49 §4: the text is the **server's own reason**, not a
      // generic failure string. A predictable 422 used to reach the user as
      // 「網路失敗」, which sent the investigation in exactly the wrong direction.
      const message = err instanceof ApiError ? err.message : "儲存失敗，請稍候重試";
      setFormError(message);
      pushToast({ variant: "danger", message });
    } finally {
      setBusy(false);
    }
  }, [title, category, owner, visibility, days, editing?.id, isEdit, onDone, draft, pushToast, queryClient]);

  const retrying = createdCount > 0 && createdCount < selectedCount;
  const primaryLabel = retrying
    ? `重試剩下的 ${selectedCount - createdCount} 天`
    : busy
      ? "儲存中…"
      : isEdit
        ? "儲存"
        : "建立事件";

  return (
    <div data-testid="calendar-event-form" className="flex flex-col pb-40">
      <PageHero
          // W52.0 D-9 — the trailing `ChevronLeft` becomes the trail's parent
          // segment. `backTo` is a computed href (the month the event lives in,
          // or the day sheet it was opened from), so the crumb carries it
          // through rather than hard-coding `/calendar`.
          //
          // The bottom 「取消」 **stays**: it cancels the edit, it does not
          // navigate up a level. Two different acts, two different controls.
          crumbs={[
            { to: backTo, label: "日曆", testId: "calendar-event-back" },
            { label: isEdit ? "編輯事件" : "新增事件" },
          ]}
          eyebrow="日曆"
          title={isEdit ? "編輯事件" : "新增事件"}
          subtitle={selectedCount > 1 ? `已選 ${selectedCount} 天` : undefined}
        />

      <FormErrorBanner message={formError} />

      <PageContainer width="default" vertical="loose">
        <SectionStack density="default">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="cal-event-title" className="font-label text-ink-2">
              標題
            </label>
            <input
              id="cal-event-title"
              type="text"
              maxLength={100}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                saveDraft({ title: e.target.value });
              }}
              placeholder="例如：整月休假"
              data-testid="calendar-event-title"
              className="h-12 rounded-md border border-hairline bg-paper px-3 text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2"
            />
          </div>

          <MiniCalendar
            cursor={cursor}
            days={days}
            miniDays={miniDays}
            onShift={(delta) => {
              const next = shiftMonth(cursor, delta);
              setCursor(next);
              saveDraft({ cursor: next });
            }}
            onToggle={toggleDay}
          />

          {/* W58.0 — 說明自動套用了什麼，並且給一個還原。
              自動改動使用者的選取是一件需要「說得出來」的事：靜靜地
              把 1 天變成 5 天，使用者會覺得表單在自作主張。 */}
          {cycleAutofill.expanded && cycleAutofill.periodLength ? (
            <div
              className="flex items-center justify-between gap-s-3 rounded-md bg-paper-deep px-s-3 py-s-2"
              data-testid="calendar-event-cycle-autofill"
            >
              <p className="text-meta text-ink-2">
                已依平均經期長度自動選了 {cycleAutofill.periodLength} 天，可自行調整。
              </p>
              <button
                type="button"
                className="min-h-11 min-w-11 px-1 -mx-1 text-meta font-semibold text-you-strong hover:underline"
                onClick={cycleAutofill.undo}
                data-testid="calendar-event-cycle-undo"
              >
                還原成 1 天
              </button>
            </div>
          ) : null}

          <fieldset className="flex flex-col gap-s-2">
            <legend className="text-micro uppercase tracking-wide text-ink-3">類型</legend>
            <div className="flex flex-wrap gap-s-2">
              {OWNED.map((c) => {
                const Icon = CATEGORY_ICON[c];
                return (
                  <Choice
                    key={c}
                    testId={`calendar-event-category-${c}`}
                    selected={category === c}
                    onSelect={() => {
                      setCategory(c);
                      saveDraft({ category: c });
                      // W58.0 — 換出週期就清掉自動套用的狀態，換回來時
                      // 才會重新考慮一次（見 `useCycleAutofill`）。
                      cycleAutofill.onCategoryChange(c);
                    }}
                  >
                    <Icon size={12} strokeWidth={2} aria-hidden="true" />
                    {CATEGORY_LABEL[c]}
                  </Choice>
                );
              })}
            </div>
          </fieldset>

          <fieldset className="flex flex-col gap-s-2">
            <legend className="text-micro uppercase tracking-wide text-ink-3">誰的</legend>
            <div className="flex flex-wrap gap-s-2">
              {(["me", "partner"] as OwnerToken[]).map((o) => (
                <Choice
                  key={o}
                  testId={`calendar-event-owner-${o}`}
                  selected={owner === o}
                  onSelect={() => {
                    setOwner(o);
                    saveDraft({ owner: o });
                  }}
                >
                  <span
                    className={cn(
                      "flex h-5 w-5 items-center justify-center rounded-pill text-micro font-semibold text-paper",
                      o === "me" ? "bg-you-strong" : "bg-partner-strong",
                    )}
                    aria-hidden="true"
                  >
                    {o === "me" ? "你" : "TA"}
                  </span>
                  {o === "me" ? "只記你的" : "只記 TA 的"}
                </Choice>
              ))}
            </div>
          </fieldset>

          <fieldset className="flex flex-col gap-s-2">
            <legend className="text-micro uppercase tracking-wide text-ink-3">可見性</legend>
            <div className="flex flex-wrap gap-s-2">
              {(["shared", "private"] as const).map((v) => (
                <Choice
                  key={v}
                  testId={`calendar-event-visibility-${v}`}
                  selected={visibility === v}
                  onSelect={() => {
                    setVisibility(v);
                    saveDraft({ visibility: v });
                  }}
                >
                  {VISIBILITY_LABEL[v]}
                </Choice>
              ))}
            </div>
            <p className="text-meta text-ink-2">
              一般事件只有建立的人能改，<strong className="font-semibold text-ink-1">週期</strong>
              例外，雙方都能改。
            </p>
          </fieldset>
        </SectionStack>
      </PageContainer>

      <PageBottomActionBar
        primary={{
          label: primaryLabel,
          onClick: () => void submit(),
          loading: busy,
          disabled: tooLong || selectedCount === 0,
          testId: "calendar-event-submit",
        }}
        back={{
          label: "取消",
          onClick: () => nav(backTo),
          testId: "calendar-event-cancel",
        }}
        testId="calendar-event-bottom-bar"
      />
    </div>
  );
}

/**
 * The mini calendar, now a tap-to-toggle multi-select.
 *
 * No pointer handlers on the grid, by design — see the file header. The 44px
 * cell size (W39.54) is kept: this is the one place a date is chosen with a
 * fingertip rather than a cursor.
 */
function MiniCalendar({
  cursor,
  days,
  miniDays,
  onShift,
  onToggle,
}: {
  cursor: string;
  days: string[];
  miniDays: string[];
  onShift: (delta: number) => void;
  onToggle: (day: string) => void;
}) {
  return (
    <section className="rounded-md border border-hairline bg-paper-deep p-s-3">
      <div className="flex items-center justify-between">
        <h3 className="text-micro uppercase tracking-wide text-ink-3">選日期 · 可以跳著選</h3>
        <div className="flex items-center gap-s-1">
          <button
            type="button"
            className="min-h-11 min-w-11 -mx-1 px-s-2 text-meta text-ink-2 hover:text-ink-1"
            onClick={() => onShift(-1)}
            aria-label="上個月"
            data-testid="calendar-mini-prev"
          >
            ‹
          </button>
          <span className="text-meta font-semibold tabular-nums text-ink-2">
            {cursor.slice(0, 7).replace("-", " / ")}
          </span>
          <button
            type="button"
            className="min-h-11 min-w-11 -mx-1 px-s-2 text-meta text-ink-2 hover:text-ink-1"
            onClick={() => onShift(1)}
            aria-label="下個月"
            data-testid="calendar-mini-next"
          >
            ›
          </button>
        </div>
      </div>

      <div className="mt-s-2 grid grid-cols-7 gap-s-1" aria-hidden="true">
        {WEEKDAY_SHORT.map((d) => (
          <div key={d} className="text-center text-micro text-ink-3">
            {d}
          </div>
        ))}
      </div>

      <div className="mt-s-1 grid grid-cols-7 gap-s-1" data-testid="calendar-mini-grid">
        {miniDays.map((day) => {
          const selected = days.includes(day);
          const outside = day.slice(0, 7) !== cursor.slice(0, 7);
          return (
            <button
              key={day}
              type="button"
              onClick={() => onToggle(day)}
              data-date={day}
              aria-pressed={selected}
              aria-label={`${day}${selected ? " 已選取" : ""}`}
              data-testid={`calendar-mini-day-${day}`}
              data-selected={selected || undefined}
              className={cn(
                // W45.0: the box comes from the SAME `--cell-h` / `--cell-r`
                // token the month grid reads, so the two grids cannot drift and
                // the re-scoped `calendarCellSize.test.tsx` can assert on the
                // source rather than on two files happening to agree.
                //
                // It is a **date picker**, so it has no chip rows and therefore
                // no second row — the 69px is mostly air below the number. That
                // is the deliberate trade: aligning the *box* is what "global
                // alignment" can mean without adding 22px of emptiness to every
                // cell of an embedded calendar.
                "calendar-cell flex items-center justify-center text-meta tabular-nums",
                outside && "opacity-30",
                selected ? "bg-you-strong font-semibold text-paper" : "text-ink-1 hover:bg-black/5",
              )}
            >
              {Number(day.slice(8, 10))}
            </button>
          );
        })}
      </div>

      <div className="mt-s-3 flex items-center justify-between">
        <span
          className="text-body-sm font-semibold tabular-nums text-you-strong"
          data-testid="calendar-event-selection"
        >
          {describeSelection(days)}
        </span>
        <span className="text-meta text-ink-3">共 {days.length} 天 · 上限 {CALENDAR_RANGE_DAYS} 天</span>
      </div>
      {/* Says which of the two storage shapes this selection will take, before
          the user commits — the D2 cost is "each day is its own event", and a
          user who only finds that out by discovering a stray 「+1」 on the 6th
          has learned it the expensive way. */}
      <p className="mt-s-1 text-meta text-ink-2" data-testid="calendar-event-storage-note">
        {isContiguous(days)
          ? "連續的日期會存成一筆。"
          : "不連續的日期會各存成一筆 —— 改標題或刪除時一次只影響其中一天。"}
      </p>
    </section>
  );
}

function Choice({
  selected,
  onSelect,
  testId,
  children,
}: {
  selected: boolean;
  onSelect: () => void;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      data-testid={testId}
      className={cn(
        "tap-target inline-flex items-center gap-s-2 rounded-pill border px-s-3 text-body-sm",
        selected
          ? "border-ink-1 bg-ink-1 text-paper"
          : "border-hairline-strong text-ink-2 hover:bg-black/5",
      )}
    >
      {children}
    </button>
  );
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** One stored row → the days it covers, so the edit page shows the same set. */
function expandRow(start: string, end: string | null): string[] {
  if (!end) return [start];
  const out: string[] = [];
  const cursor = new Date(`${start}T00:00:00Z`);
  const last = new Date(`${end}T00:00:00Z`);
  while (cursor <= last && out.length <= CALENDAR_RANGE_DAYS) {
    out.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return out;
}

function describeSelection(days: readonly string[]): string {
  if (days.length === 0) return "尚未選日期";
  const sorted = [...days].sort();
  const first = sorted[0]!;
  if (sorted.length === 1) return formatMd(first);
  if (isContiguous(sorted)) {
    return `${formatMd(first)} – ${formatMd(sorted[sorted.length - 1]!)}`;
  }
  return sorted.map(formatMd).join("、");
}

function formatMd(d: string): string {
  return `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}`;
}

function shiftMonth(isoDate: string, delta: number): string {
  const y = Number(isoDate.slice(0, 4));
  const m = Number(isoDate.slice(5, 7));
  const d = Number(isoDate.slice(8, 10));
  const dt = new Date(Date.UTC(y, m - 1 + delta, 1));
  const lastDay = new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth() + 1, 0)).getUTCDate();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  return `${dt.getUTCFullYear()}-${mm}-${String(Math.min(d, lastDay)).padStart(2, "0")}`;
}

function buildMiniMonth(cursor: string): string[] {
  const y = Number(cursor.slice(0, 4));
  const m = Number(cursor.slice(5, 7));
  const first = new Date(Date.UTC(y, m - 1, 1));
  const lead = (first.getUTCDay() + 6) % 7; // Monday-first
  const start = new Date(Date.UTC(y, m - 1, 1 - lead));
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setUTCDate(d.getUTCDate() + i);
    return d.toISOString().slice(0, 10);
  });
}
